import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { ALLIES_CONTENT } from "../data/allies";

const scrollTo = (id) => {
  if (window.__lenis) window.__lenis.scrollTo(id, { offset: -72, duration: 1.4 });
  else document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = ({ t, lang, setLang }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "#services", label: t.nav.services, testid: "nav-link-services" },
    { id: "#allies", label: ALLIES_CONTENT.navLabel[lang], testid: "nav-link-allies" },
    { id: "#offices", label: t.nav.offices, testid: "nav-link-offices" },
    { id: "#contact", label: t.nav.contact, testid: "nav-link-contact" },
  ];

  const go = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    scrollTo(id);
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "border-b border-white/10 bg-[#0A0D14]/85 backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <a href="#top" data-testid="nav-logo-link" onClick={go("#top")} aria-label="Sagas Logistic">
          <Logo compact />
        </a>

        <nav className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.id}
              data-testid={l.testid}
              onClick={go(l.id)}
              className="text-sm font-medium tracking-wide text-slate-300 transition-colors duration-300 hover:text-amber-400"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div data-testid="nav-language-select" className="flex items-center rounded-full border border-white/15 p-1">
            {["es", "en", "pt"].map((code) => (
              <button
                key={code}
                data-testid={`nav-lang-${code}`}
                onClick={() => setLang(code)}
                className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest transition-colors duration-300 ${
                  lang === code ? "bg-amber-400 text-slate-950" : "text-slate-400 hover:text-white"
                }`}
              >
                {code}
              </button>
            ))}
          </div>
          <a
            href="#contact"
            data-testid="nav-cta-button"
            onClick={go("#contact")}
            className="hidden rounded-full bg-amber-400 px-5 py-2.5 text-sm font-bold text-slate-950 transition-colors duration-300 hover:bg-amber-300 md:block"
          >
            {t.nav.cta}
          </a>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="text-white md:hidden"
            aria-label="Menu"
          >
            {open ? <Menu className="hidden" /> : <Menu />}
            {open && <X className="absolute right-5 top-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="border-t border-white/10 bg-[#0A0D14]/95 px-5 py-6 backdrop-blur-xl md:hidden">
          <div className="flex flex-col gap-5">
            {links.map((l) => (
              <a key={l.id} href={l.id} data-testid={`${l.testid}-mobile`} onClick={go(l.id)} className="text-lg font-medium text-slate-200">
                {l.label}
              </a>
            ))}
            <a href="#contact" data-testid="nav-cta-button-mobile" onClick={go("#contact")} className="mt-2 rounded-full bg-amber-400 px-5 py-3 text-center text-sm font-bold text-slate-950">
              {t.nav.cta}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
